/* eslint-disable @next/next/no-img-element */
import { unit, C, Canvas, Abs, Card, Avatar, BLogo, Icon, Sparkle } from './kit';

// Vector rebuild of /img/redesign/measure-visual-v4.png (1448×1086 canvas).
const W = 1448;
const H = 1086;
const u = unit(W);

const INK = '#15122A';
const GREEN = '#22B33A';
const PURPLE_ARROW = '#AC89F2';

// Text line positioned by the top of its capitals (as measured on the PNG).
const T = ({ x, cap, size, weight = 500, color = INK, style, children }) => (
  <Abs u={u} x={x} y={cap - size * 0.14} style={{
    fontSize: u(size), fontWeight: weight, color, lineHeight: 1, ...style,
  }}>
    {children}
  </Abs>
);

// Dashed hand-drawn connector arrows (thicker than the kit ones, as in this render).
const ARROWS = [
  { d: 'M1236 94 C1250 70 1272 71 1290 72 C1312 73 1330 60 1339 35', head: [1341, 30, -68] },
  { d: 'M1196 498 C1197 532 1176 564 1131 577', head: [1126, 578, 165] },
  { d: 'M113 729 C112 700 92 690 76 666 C54 634 64 594 114 581', head: [119, 580, -15] },
  { d: 'M733 1004 C690 1003 652 986 648 937 C646 906 670 884 699 875', head: [704, 873, -22] },
];

const MeasureArrows = () => (
  <svg viewBox={`0 0 ${W} ${H}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }} aria-hidden="true">
    {ARROWS.map((a, i) => (
      <g key={i} stroke={PURPLE_ARROW} fill="none" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round">
        <path d={a.d} strokeDasharray="9 9" />
        <path d="M-11 -9 L0 0 L-11 9" transform={`translate(${a.head[0]} ${a.head[1]}) rotate(${a.head[2]})`} strokeWidth="5.5" />
      </g>
    ))}
  </svg>
);

const SideIcon = ({ name, cy, size = 36, stroke = 2.4 }) => (
  <Abs u={u} x={224 - size / 2} y={cy - size / 2} style={{ color: '#fff' }}>
    <Icon u={u} name={name} size={size} stroke={stroke} />
  </Abs>
);

const EMOJIS = ['😍', '😀', '😐', '😟', '😘'];

const GAUGE = [
  { from: 36, to: 141, color: '#E92A2E', round: 'both' },
  { from: 143, to: 220, color: '#F6B013', round: 'both' },
  { from: 222, to: 302, color: '#2BBA3C', round: 'left' },
  { from: 302, to: 357, color: '#1A6FF5', round: 'right' },
];

const LEGEND = [
  { label: 'Promoters', value: '62%', color: '#2DBA3F', cap: 258 },
  { label: 'Passives', value: '23%', color: '#F5AF14', cap: 297 },
  { label: 'Detractors', value: '15%', color: '#E5333A', cap: 336 },
];

const RATINGS = [
  { n: 5, fill: 116, color: '#26B437', pct: '62%' },
  { n: 4, fill: 58, color: '#2CBB3D', pct: '28%' },
  { n: 3, fill: 24, color: '#F7AC07', pct: '8%' },
  { n: 2, fill: 12, color: '#E8303A', pct: '2%' },
  { n: 1, fill: 0, color: 'transparent', pct: '0%' },
];

// Donut arc on a circle of radius R centred in a 2R+stroke box; angles in degrees clockwise from 12 o'clock.
const arc = (cx, cy, r, a0, a1) => {
  const p = (a) => {
    const rad = ((a - 90) * Math.PI) / 180;
    return `${(cx + r * Math.cos(rad)).toFixed(2)} ${(cy + r * Math.sin(rad)).toFixed(2)}`;
  };
  return `M${p(a0)} A${r} ${r} 0 ${a1 - a0 > 180 ? 1 : 0} 1 ${p(a1)}`;
};

const Chevron = ({ x, y, size = 12, color }) => (
  <Abs u={u} x={x} y={y}>
    <Icon u={u} name="chevronDown" size={size} color={color} stroke={2.6} />
  </Abs>
);

export default function MeasureVisual({ className }) {
  return (
    <Canvas W={W} H={H} className={className} label="Measure: Pulsy pulse-survey in Slack, eNPS score, response rate trend and performance overview">
      <MeasureArrows />

      <Sparkle u={u} x={97} y={116} size={25} color="#F5CF6E" />
      <Sparkle u={u} x={114} y={176} size={26} color="#BCA3FC" />
      <Sparkle u={u} x={1327} y={590} size={25} color="#F5CF6E" />
      <Sparkle u={u} x={1344} y={704} size={27} color="#B79DFA" />

      {/* App window frame */}
      <Abs u={u} x={160} y={22} w={947} h={730} style={{
        background: 'linear-gradient(180deg, #F4EFF8 0%, #F1ECF6 100%)', borderRadius: u(32),
        border: `${u(1.5)} solid #FFFFFF`, boxSizing: 'border-box',
        boxShadow: `0 ${u(12)} ${u(34)} rgba(40, 30, 80, 0.08)`,
      }} />

      {/* Sidebar */}
      <Abs u={u} x={172} y={37} w={105} h={685} style={{
        background: 'linear-gradient(180deg, #1A132D 0%, #1E1733 100%)', borderRadius: u(22),
      }} />
      <Abs u={u} x={192} y={58}><BLogo u={u} size={66} /></Abs>
      <SideIcon name="home" cy={180} size={44} />
      <SideIcon name="bot" cy={255} size={45} />
      <SideIcon name="bell" cy={334} size={45} stroke={2.6} />
      <Abs u={u} x={193} y={389} w={64} h={60} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#6646F6', borderRadius: u(12), color: '#fff',
      }}>
        <Icon u={u} name="hash" size={37} stroke={2.8} />
      </Abs>
      <SideIcon name="plus" cy={493} size={43} stroke={1.9} />
      <Abs u={u} x={191.5} y={627}><Avatar u={u} name="me" size={64} dot="#1E1733" /></Abs>

      {/* Channel */}
      <Abs u={u} x={280} y={36} w={811} h={700} style={{
        background: '#F9F7FA', borderRadius: u(22), border: `${u(1.5)} solid #ECE6F1`, boxSizing: 'border-box',
      }} />
      <T x={325} cap={71} size={32} weight={700}>#</T>
      <T x={364} cap={72} size={29.6} weight={700}>pulse-survey</T>

      <Abs u={u} x={322} y={149} w={72} h={70} style={{ background: '#C9E8FF', borderRadius: u(12), overflow: 'hidden' }}>
        <img src="/img/redesign/mascots/pulsy.webp" alt="" draggable="false" style={{
          position: 'absolute', left: u(4), top: u(3), width: u(63), height: u(63),
        }} />
      </Abs>
      <T x={416} cap={156} size={22} weight={700}>Pulsy</T>
      <Abs u={u} x={481} y={156} w={38} h={19} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#E9E6EF', borderRadius: u(5), fontSize: u(13.5), fontWeight: 700, color: INK, letterSpacing: '0.02em',
      }}>APP</Abs>
      <T x={538} cap={160} size={17.5} color="#9693BB">10:42 AM</T>
      <T x={416} cap={194.5} size={20.5} weight={700}>
        How’s this week going?<span style={{ fontSize: u(25), marginLeft: u(12) }}>💙</span>
      </T>
      <T x={415} cap={236} size={21}>This will only take 30 seconds.</T>

      <T x={413} cap={316} size={18} weight={700}>1. Overall, how would you rate this week?</T>
      <T x={413} cap={353} size={16.5} color="#8380A8">Select one</T>
      <Chevron x={500} y={354} size={13} color="#8A87AF" />
      {EMOJIS.map((e, i) => (
        <Abs key={e} u={u} x={413 + i * 82.8} y={393} w={82} h={73} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#EEF1F6', border: `${u(1.5)} solid #DDE4EF`, borderRadius: u(10), boxSizing: 'border-box',
          fontSize: u(33), lineHeight: 1,
        }}>{e}</Abs>
      ))}

      <T x={413} cap={515} size={18} weight={700}>2. What’s one thing we could do better?</T>
      <T x={413} cap={549} size={18} color="#9592B8">Share your honest feedback…</T>
      <Abs u={u} x={413} y={583} w={422} h={58} style={{
        background: '#FAF8FB', border: `${u(1.5)} solid #E2DEEA`, borderRadius: u(10), boxSizing: 'border-box',
      }} />
      <T x={432} cap={606} size={17.5} color="#ABA8C8">Type your answer…</T>
      <Abs u={u} x={413} y={662} w={100} h={45} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#6142F5', borderRadius: u(8), color: '#fff', fontSize: u(17), fontWeight: 600,
      }}>Submit</Abs>

      {/* eNPS score */}
      <Card u={u} x={1004} y={98.5} w={390} h={390} r={24} bg="#F9F7FA" style={{ transform: 'rotate(3.8deg)' }}>
        <T x={42} cap={34} size={24} weight={700}>eNPS Score</T>
        <Abs u={u} x={186} y={31}><Icon u={u} name="info" size={23} color="#9F97CF" stroke={2} /></Abs>
        <T x={45} cap={92} size={50} weight={700} style={{ letterSpacing: '-0.03em' }}>+42</T>
        <Abs u={u} x={159} y={110}><Icon u={u} name="arrowUp" size={16} color={GREEN} stroke={2.6} /></Abs>
        <T x={181} cap={111} size={18} weight={600} color={GREEN} style={{ letterSpacing: '-0.02em' }}>18</T>
        <T x={203} cap={111} size={18}>from last month</T>
        {GAUGE.map(s => (
          <Abs key={s.from} u={u} x={s.from} y={168} w={s.to - s.from} h={12} style={{
            background: s.color,
            borderRadius: s.round === 'both' ? u(6) : s.round === 'left' ? `${u(6)} 0 0 ${u(6)}` : `0 ${u(6)} ${u(6)} 0`,
          }} />
        ))}
        <Abs u={u} x={283} y={164} w={20} h={20} style={{ borderRadius: '50%', background: '#25AE38' }} />
        {[['-100', 49.5], ['-50', 120], ['0', 198], ['50', 277], ['100', 346.5]].map(([t, cx]) => (
          <T key={t} x={cx - 30} cap={200} size={17} color="#9A97BD" style={{ width: u(60), textAlign: 'center' }}>{t}</T>
        ))}
        {LEGEND.map(l => (
          <div key={l.label}>
            <Abs u={u} x={35} y={l.cap - 1} w={13} h={13} style={{ borderRadius: '50%', background: l.color }} />
            <T x={60} cap={l.cap} size={17.5}>{l.label}</T>
            <T x={233} cap={l.cap} size={16.5} weight={600} style={{ width: u(60), textAlign: 'right' }}>{l.value}</T>
          </div>
        ))}
      </Card>

      {/* Response rate */}
      <Card u={u} x={16} y={726.5} w={410} h={287} r={24} bg="#F9F7FA" style={{ transform: 'rotate(-2.3deg)' }}>
        <T x={36} cap={34} size={21.5} weight={700}>Response Rate</T>
        <T x={35} cap={82} size={43} weight={700} style={{ letterSpacing: '-0.03em' }}>68%</T>
        <Abs u={u} x={137} y={95}><Icon u={u} name="arrowUp" size={14} color={GREEN} stroke={2.6} /></Abs>
        <T x={154} cap={97} size={16.5} weight={600} color={GREEN}>24%</T>
        <T x={194} cap={97} size={17}>from last survey</T>
        <svg viewBox="0 0 410 287" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} aria-hidden="true">
          <defs>
            <linearGradient id="measure-rr-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#B7CBF5" stopOpacity="0.75" />
              <stop offset="1" stopColor="#E4EAF8" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d="M30 233 L96 193 C108 186 116 186 128 187 L152 190 C164 192 172 186 181 176 L198 157 C207 148 216 146 228 152 L258 167 C268 172 276 172 286 167 L366 125 L366 282 L30 282 Z"
            fill="url(#measure-rr-fill)"
          />
          <path
            d="M30 233 L96 193 C108 186 116 186 128 187 L152 190 C164 192 172 186 181 176 L198 157 C207 148 216 146 228 152 L258 167 C268 172 276 172 286 167 L366 125"
            fill="none" stroke="#1A6AF0" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round"
          />
          <circle cx="366" cy="125" r="11" fill="#1A6AF0" />
        </svg>
      </Card>

      {/* Performance overview */}
      <Card u={u} x={738.5} y={664.5} w={514} h={355} r={24} bg="#F9F7FA" style={{ transform: 'rotate(0.6deg)' }}>
        <T x={42} cap={37} size={22} weight={700}>Performance Overview</T>
        <T x={361} cap={40} size={16.5}>This Quarter</T>
        <Chevron x={457} y={40} size={13} color={INK} />
        <svg viewBox="0 0 514 355" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} aria-hidden="true">
          <g fill="none" strokeWidth="23" strokeLinecap="round">
            <circle cx="128" cy="209" r="82.5" stroke="#AFDFBA" />
            <path d={arc(128, 209, 82.5, 85, 210)} stroke="#F5A706" />
            <path d={arc(128, 209, 82.5, 2, 68)} stroke="#28B23A" />
          </g>
        </svg>
        <T x={78} cap={182} size={37} weight={700} style={{ width: u(100), textAlign: 'center' }}>4.6</T>
        <T x={67} cap={224} size={16.5} style={{ width: u(120), textAlign: 'center' }}>Avg. Rating</T>
        {RATINGS.map((r, i) => {
          const cy = 119 + i * 44;
          return (
            <div key={r.n}>
              <T x={235} cap={cy - 6} size={17} weight={600}>{r.n}</T>
              <Abs u={u} x={256} y={cy - 10}><Icon u={u} name="star" size={20} color="#F6B21A" /></Abs>
              <Abs u={u} x={279} y={cy - 7.5} w={139} h={15} style={{ background: '#EEEBF0', borderRadius: u(7.5) }}>
                {r.fill > 0 && <div style={{ width: u(r.fill), height: '100%', borderRadius: u(7.5), background: r.color }} />}
              </Abs>
              <T x={441} cap={cy - 6} size={17} weight={600}>{r.pct}</T>
            </div>
          );
        })}
      </Card>
    </Canvas>
  );
}
